import {
  JsonToBoolean,
  JsonToDouble,
  JsonToInt32,
  JsonToInt64,
  JsonToString,
  JsonToList,
  ArrayToJson,
  JsonToMap,
  MapToJson,
} from '../Helper/Converter';
export class SpawnPattern {
  get descriptor() {
    return SpawnPattern.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasInterval = false;
    this._interval = null;
    this._hasDispersal = false;
    this._dispersal = null;
  }
  isEmpty() {
    if (this._hasInterval && !this._interval.isEmpty()) return false;
    if (this._hasDispersal && !this._dispersal.isEmpty()) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof SpawnPattern) {
      return value;
    }
    return SpawnPattern.fromJson(value);
  }
  static fromJson(json) {
    const result = new SpawnPattern();
    if (json.interval != null) {
      result._hasInterval = true;
      result._interval = SpawnPatternInterval.fromJson(json.interval);
    }
    if (json.dispersal != null) {
      result._hasDispersal = true;
      result._dispersal = SpawnPatternDispersal.fromJson(json.dispersal);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasInterval && !this._interval.isEmpty()) {
      result.interval = SpawnPatternInterval.toJson(this._interval);
    }
    if (this._hasDispersal && !this._dispersal.isEmpty()) {
      result.dispersal = SpawnPatternDispersal.toJson(this._dispersal);
    }
    return result;
  }
  /**
   * @returns {SpawnPatternInterval}
   */
  get interval() {
    return this._hasInterval ? this._interval : SpawnPatternInterval.default;
  }
  get hasInterval() {
    return this._hasInterval;
  }
  setInterval(path, value) {
    return {type: 'SET', path: path.concat(['interval']), value: value.toJson()};
  }
  deleteInterval(path) {
    return {type: 'DELETE', path: path.concat(['interval'])};
  }
  /**
   * @returns {SpawnPatternDispersal}
   */
  get dispersal() {
    return this._hasDispersal ? this._dispersal : SpawnPatternDispersal.default;
  }
  get hasDispersal() {
    return this._hasDispersal;
  }
  setDispersal(path, value) {
    return {type: 'SET', path: path.concat(['dispersal']), value: value.toJson()};
  }
  deleteDispersal(path) {
    return {type: 'DELETE', path: path.concat(['dispersal'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "interval": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new SpawnPattern(this);
              copy._hasInterval = true;
              copy._interval = SpawnPatternInterval.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasInterval) {
                return this;
              }
              const copy = new SpawnPattern(this);
              copy._hasInterval = false;
              copy._interval = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (!this._hasInterval) {
            const copy = new SpawnPattern(this);
            copy._hasInterval = true;
            copy._interval = SpawnPatternInterval.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this._interval.apply(type, path.slice(1), value);
          if (newValue === this._interval) {
            return this;
          }
          const copy = new SpawnPattern(this);
          copy._interval = copy._interval.apply(type, path.slice(1), value);
          return copy;
        }
      }
      case "dispersal": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new SpawnPattern(this);
              copy._hasDispersal = true;
              copy._dispersal = SpawnPatternDispersal.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasDispersal) {
                return this;
              }
              const copy = new SpawnPattern(this);
              copy._hasDispersal = false;
              copy._dispersal = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          if (!this._hasDispersal) {
            const copy = new SpawnPattern(this);
            copy._hasDispersal = true;
            copy._dispersal = SpawnPatternDispersal.default.apply(type, path.slice(1), value);
            return copy;
          }
          const newValue = this._dispersal.apply(type, path.slice(1), value);
          if (newValue === this._dispersal) {
            return this;
          }
          const copy = new SpawnPattern(this);
          copy._dispersal = copy._dispersal.apply(type, path.slice(1), value);
          return copy;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {SpawnPattern} other
   * @returns {SpawnPattern}
   */
  merge(other) {
    const copy = new SpawnPattern(this);
    if (other._hasInterval) {
      if (copy._hasInterval) {
        copy._interval = copy.interval.merge(other.interval);
      } else {
        copy._interval = other._interval;
      }
      copy._hasInterval = true;
    }
    if (other._hasDispersal) {
      if (copy._hasDispersal) {
        copy._dispersal = copy.dispersal.merge(other.dispersal);
      } else {
        copy._dispersal = other._dispersal;
      }
      copy._hasDispersal = true;
    }
    return copy;
  }
}
SpawnPattern.default = new SpawnPattern();
class SpawnPattern_Builder {
  constructor(value) {
    this._value = new SpawnPattern(value);
  }
  build() {
    return new SpawnPattern(this._value);
  }
  setInterval(value) {
    this._value._hasInterval = true;
    this._value._interval = value;
    return this;
  }
  deleteInterval() {
    this._value._hasInterval = false;
    this._value._interval = null;
    return this;
  }
  setDispersal(value) {
    this._value._hasDispersal = true;
    this._value._dispersal = value;
    return this;
  }
  deleteDispersal() {
    this._value._hasDispersal = false;
    this._value._dispersal = null;
    return this;
  }
}
SpawnPattern.Builder = SpawnPattern_Builder;
class SpawnPattern_WithParent {
  /**
   * @param {SpawnPattern} value
   * @param {SpawnPattern_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return SpawnPattern.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasInterval() {
    if (this._cachedHasInterval === undefined) {
      if (this._value.hasInterval) {
        this._cachedHasInterval = true;
      } else {
        this._cachedHasInterval = this._parent ? this._parent.hasInterval : false;
      }
    }
    return this._cachedHasInterval;
  }
  get interval() {
    if (!this._cachedInterval) {
      if (this._value.hasInterval) {
        this._cachedInterval = new SpawnPatternInterval.WithParent(this._value.interval, this._parent && this._parent.hasInterval ? this._parent.interval : null);
      } else {
        this._cachedInterval = this._parent ? new SpawnPatternInterval.WithParent(SpawnPatternInterval.default, this._parent.interval) : SpawnPatternInterval.WithParent.default;
      }
    }
    return this._cachedInterval;
  }
  get hasDispersal() {
    if (this._cachedHasDispersal === undefined) {
      if (this._value.hasDispersal) {
        this._cachedHasDispersal = true;
      } else {
        this._cachedHasDispersal = this._parent ? this._parent.hasDispersal : false;
      }
    }
    return this._cachedHasDispersal;
  }
  get dispersal() {
    if (!this._cachedDispersal) {
      if (this._value.hasDispersal) {
        this._cachedDispersal = new SpawnPatternDispersal.WithParent(this._value.dispersal, this._parent && this._parent.hasDispersal ? this._parent.dispersal : null);
      } else {
        this._cachedDispersal = this._parent ? new SpawnPatternDispersal.WithParent(SpawnPatternDispersal.default, this._parent.dispersal) : SpawnPatternDispersal.WithParent.default;
      }
    }
    return this._cachedDispersal;
  }
  update(current, parent) {
    const result = new SpawnPattern_WithParent(current, parent);
    if (this._cachedInterval && this._value.hasInterval && current.hasInterval) {
      if (this._value.interval === current.interval && this._parent === parent) {
        result._cachedInterval = this._cachedInterval;
      } else {
        result._cachedInterval = this._cachedInterval.update(current.interval, parent && parent.hasInterval ? parent.interval : null);
      }
    }
    if (this._cachedDispersal && this._value.hasDispersal && current.hasDispersal) {
      if (this._value.dispersal === current.dispersal && this._parent === parent) {
        result._cachedDispersal = this._cachedDispersal;
      } else {
        result._cachedDispersal = this._cachedDispersal.update(current.dispersal, parent && parent.hasDispersal ? parent.dispersal : null);
      }
    }
    return result;
  }
}
SpawnPattern_WithParent.default = new SpawnPattern_WithParent(SpawnPattern.default, null);
SpawnPattern.WithParent = SpawnPattern_WithParent;
class SpawnPattern_Descriptor {
  get interval() {
    if (!this._interval) {
      this._interval = {
        name: "interval",
        Name: "Interval",
        number: 1,
        protoType: 11,
        protoName: "interval",
        type: SpawnPatternInterval,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._interval;
  }
  get dispersal() {
    if (!this._dispersal) {
      this._dispersal = {
        name: "dispersal",
        Name: "Dispersal",
        number: 2,
        protoType: 11,
        protoName: "dispersal",
        type: SpawnPatternDispersal,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._dispersal;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.interval,
        this.dispersal,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForInterval",
        Name: "DummyOneofForInterval",
        fields: [
          this.fields[0],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForDispersal",
        Name: "DummyOneofForDispersal",
        fields: [
          this.fields[1],
        ],
      });
    }
    return this._oneofs;
  }
}
SpawnPattern.descriptor = new SpawnPattern_Descriptor();
export class SpawnPatternInterval {
  get descriptor() {
    return SpawnPatternInterval.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasMin = false;
    this._min = null;
    this._hasMax = false;
    this._max = null;
  }
  isEmpty() {
    if (this._hasMin) return false;
    if (this._hasMax) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof SpawnPatternInterval) {
      return value;
    }
    return SpawnPatternInterval.fromJson(value);
  }
  static fromJson(json) {
    const result = new SpawnPatternInterval();
    if (json.min != null) {
      result._hasMin = true;
      result._min = JsonToDouble.fromJson(json.min);
    }
    if (json.max != null) {
      result._hasMax = true;
      result._max = JsonToDouble.fromJson(json.max);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasMin) {
      result.min = JsonToDouble.toJson(this._min);
    }
    if (this._hasMax) {
      result.max = JsonToDouble.toJson(this._max);
    }
    return result;
  }
  /**
   * @returns {number}
   */
  get min() {
    return this._hasMin ? this._min : JsonToDouble.default;
  }
  get hasMin() {
    return this._hasMin;
  }
  setMin(path, value) {
    return {type: 'SET', path: path.concat(['min']), value};
  }
  deleteMin(path) {
    return {type: 'DELETE', path: path.concat(['min'])};
  }
  /**
   * @returns {number}
   */
  get max() {
    return this._hasMax ? this._max : JsonToDouble.default;
  }
  get hasMax() {
    return this._hasMax;
  }
  setMax(path, value) {
    return {type: 'SET', path: path.concat(['max']), value};
  }
  deleteMax(path) {
    return {type: 'DELETE', path: path.concat(['max'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "min": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new SpawnPatternInterval(this);
              copy._hasMin = true;
              copy._min = JsonToDouble.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasMin) {
                return this;
              }
              const copy = new SpawnPatternInterval(this);
              copy._hasMin = false;
              copy._min = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "max": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new SpawnPatternInterval(this);
              copy._hasMax = true;
              copy._max = JsonToDouble.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasMax) {
                return this;
              }
              const copy = new SpawnPatternInterval(this);
              copy._hasMax = false;
              copy._max = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {SpawnPatternInterval} other
   * @returns {SpawnPatternInterval}
   */
  merge(other) {
    const copy = new SpawnPatternInterval(this);
    if (other._hasMin) {
      copy._hasMin = true;
      copy._min = other._min;
    }
    if (other._hasMax) {
      copy._hasMax = true;
      copy._max = other._max;
    }
    return copy;
  }
}
SpawnPatternInterval.default = new SpawnPatternInterval();
class SpawnPatternInterval_Builder {
  constructor(value) {
    this._value = new SpawnPatternInterval(value);
  }
  build() {
    return new SpawnPatternInterval(this._value);
  }
  setMin(value) {
    this._value._hasMin = true;
    this._value._min = value;
    return this;
  }
  deleteMin() {
    this._value._hasMin = false;
    this._value._min = null;
    return this;
  }
  setMax(value) {
    this._value._hasMax = true;
    this._value._max = value;
    return this;
  }
  deleteMax() {
    this._value._hasMax = false;
    this._value._max = null;
    return this;
  }
}
SpawnPatternInterval.Builder = SpawnPatternInterval_Builder;
class SpawnPatternInterval_WithParent {
  /**
   * @param {SpawnPatternInterval} value
   * @param {SpawnPatternInterval_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return SpawnPatternInterval.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasMin() {
    if (this._cachedHasMin === undefined) {
      if (this._value.hasMin) {
        this._cachedHasMin = true;
      } else {
        this._cachedHasMin = this._parent ? this._parent.hasMin : false;
      }
    }
    return this._cachedHasMin;
  }
  get min() {
    if (this._cachedMin === undefined) {
      if (this._value.hasMin) {
        this._cachedMin = this._value.min;
      } else {
        this._cachedMin = this._parent ? this._parent.min : this._value.min;
      }
    }
    return this._cachedMin;
  }
  get hasMax() {
    if (this._cachedHasMax === undefined) {
      if (this._value.hasMax) {
        this._cachedHasMax = true;
      } else {
        this._cachedHasMax = this._parent ? this._parent.hasMax : false;
      }
    }
    return this._cachedHasMax;
  }
  get max() {
    if (this._cachedMax === undefined) {
      if (this._value.hasMax) {
        this._cachedMax = this._value.max;
      } else {
        this._cachedMax = this._parent ? this._parent.max : this._value.max;
      }
    }
    return this._cachedMax;
  }
  update(current, parent) {
    const result = new SpawnPatternInterval_WithParent(current, parent);
    return result;
  }
}
SpawnPatternInterval_WithParent.default = new SpawnPatternInterval_WithParent(SpawnPatternInterval.default, null);
SpawnPatternInterval.WithParent = SpawnPatternInterval_WithParent;
class SpawnPatternInterval_Descriptor {
  get min() {
    if (!this._min) {
      this._min = {
        name: "min",
        Name: "Min",
        number: 1,
        protoType: 1,
        protoName: "min",
        type: JsonToDouble,
        options: {
          propertyRange: "0,\"Max\"",
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._min;
  }
  get max() {
    if (!this._max) {
      this._max = {
        name: "max",
        Name: "Max",
        number: 2,
        protoType: 1,
        protoName: "max",
        type: JsonToDouble,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._max;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.min,
        this.max,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForMin",
        Name: "DummyOneofForMin",
        fields: [
          this.fields[0],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForMax",
        Name: "DummyOneofForMax",
        fields: [
          this.fields[1],
        ],
      });
    }
    return this._oneofs;
  }
}
SpawnPatternInterval.descriptor = new SpawnPatternInterval_Descriptor();
export class SpawnPatternDispersal {
  get descriptor() {
    return SpawnPatternDispersal.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasMin = false;
    this._min = null;
    this._hasMax = false;
    this._max = null;
  }
  isEmpty() {
    if (this._hasMin) return false;
    if (this._hasMax) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof SpawnPatternDispersal) {
      return value;
    }
    return SpawnPatternDispersal.fromJson(value);
  }
  static fromJson(json) {
    const result = new SpawnPatternDispersal();
    if (json.min != null) {
      result._hasMin = true;
      result._min = JsonToDouble.fromJson(json.min);
    }
    if (json.max != null) {
      result._hasMax = true;
      result._max = JsonToDouble.fromJson(json.max);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasMin) {
      result.min = JsonToDouble.toJson(this._min);
    }
    if (this._hasMax) {
      result.max = JsonToDouble.toJson(this._max);
    }
    return result;
  }
  /**
   * @returns {number}
   */
  get min() {
    return this._hasMin ? this._min : JsonToDouble.default;
  }
  get hasMin() {
    return this._hasMin;
  }
  setMin(path, value) {
    return {type: 'SET', path: path.concat(['min']), value};
  }
  deleteMin(path) {
    return {type: 'DELETE', path: path.concat(['min'])};
  }
  /**
   * @returns {number}
   */
  get max() {
    return this._hasMax ? this._max : JsonToDouble.default;
  }
  get hasMax() {
    return this._hasMax;
  }
  setMax(path, value) {
    return {type: 'SET', path: path.concat(['max']), value};
  }
  deleteMax(path) {
    return {type: 'DELETE', path: path.concat(['max'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "min": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new SpawnPatternDispersal(this);
              copy._hasMin = true;
              copy._min = JsonToDouble.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasMin) {
                return this;
              }
              const copy = new SpawnPatternDispersal(this);
              copy._hasMin = false;
              copy._min = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "max": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new SpawnPatternDispersal(this);
              copy._hasMax = true;
              copy._max = JsonToDouble.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasMax) {
                return this;
              }
              const copy = new SpawnPatternDispersal(this);
              copy._hasMax = false;
              copy._max = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {SpawnPatternDispersal} other
   * @returns {SpawnPatternDispersal}
   */
  merge(other) {
    const copy = new SpawnPatternDispersal(this);
    if (other._hasMin) {
      copy._hasMin = true;
      copy._min = other._min;
    }
    if (other._hasMax) {
      copy._hasMax = true;
      copy._max = other._max;
    }
    return copy;
  }
}
SpawnPatternDispersal.default = new SpawnPatternDispersal();
class SpawnPatternDispersal_Builder {
  constructor(value) {
    this._value = new SpawnPatternDispersal(value);
  }
  build() {
    return new SpawnPatternDispersal(this._value);
  }
  setMin(value) {
    this._value._hasMin = true;
    this._value._min = value;
    return this;
  }
  deleteMin() {
    this._value._hasMin = false;
    this._value._min = null;
    return this;
  }
  setMax(value) {
    this._value._hasMax = true;
    this._value._max = value;
    return this;
  }
  deleteMax() {
    this._value._hasMax = false;
    this._value._max = null;
    return this;
  }
}
SpawnPatternDispersal.Builder = SpawnPatternDispersal_Builder;
class SpawnPatternDispersal_WithParent {
  /**
   * @param {SpawnPatternDispersal} value
   * @param {SpawnPatternDispersal_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return SpawnPatternDispersal.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasMin() {
    if (this._cachedHasMin === undefined) {
      if (this._value.hasMin) {
        this._cachedHasMin = true;
      } else {
        this._cachedHasMin = this._parent ? this._parent.hasMin : false;
      }
    }
    return this._cachedHasMin;
  }
  get min() {
    if (this._cachedMin === undefined) {
      if (this._value.hasMin) {
        this._cachedMin = this._value.min;
      } else {
        this._cachedMin = this._parent ? this._parent.min : this._value.min;
      }
    }
    return this._cachedMin;
  }
  get hasMax() {
    if (this._cachedHasMax === undefined) {
      if (this._value.hasMax) {
        this._cachedHasMax = true;
      } else {
        this._cachedHasMax = this._parent ? this._parent.hasMax : false;
      }
    }
    return this._cachedHasMax;
  }
  get max() {
    if (this._cachedMax === undefined) {
      if (this._value.hasMax) {
        this._cachedMax = this._value.max;
      } else {
        this._cachedMax = this._parent ? this._parent.max : this._value.max;
      }
    }
    return this._cachedMax;
  }
  update(current, parent) {
    const result = new SpawnPatternDispersal_WithParent(current, parent);
    return result;
  }
}
SpawnPatternDispersal_WithParent.default = new SpawnPatternDispersal_WithParent(SpawnPatternDispersal.default, null);
SpawnPatternDispersal.WithParent = SpawnPatternDispersal_WithParent;
class SpawnPatternDispersal_Descriptor {
  get min() {
    if (!this._min) {
      this._min = {
        name: "min",
        Name: "Min",
        number: 1,
        protoType: 1,
        protoName: "min",
        type: JsonToDouble,
        options: {
          max: 1,
          propertyRange: "0,\"Max\"",
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._min;
  }
  get max() {
    if (!this._max) {
      this._max = {
        name: "max",
        Name: "Max",
        number: 2,
        protoType: 1,
        protoName: "max",
        type: JsonToDouble,
        options: {
          min: 0,
          propertyRange: "\"Min\", 1",
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._max;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.min,
        this.max,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForMin",
        Name: "DummyOneofForMin",
        fields: [
          this.fields[0],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForMax",
        Name: "DummyOneofForMax",
        fields: [
          this.fields[1],
        ],
      });
    }
    return this._oneofs;
  }
}
SpawnPatternDispersal.descriptor = new SpawnPatternDispersal_Descriptor();
export class SpawnGroupPatternUnit {
  get descriptor() {
    return SpawnGroupPatternUnit.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._hasUnit = false;
    this._unit = null;
    this._hasDelay = false;
    this._delay = null;
    this._hasOffset = false;
    this._offset = null;
  }
  isEmpty() {
    if (this._hasUnit) return false;
    if (this._hasDelay) return false;
    if (this._hasOffset) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof SpawnGroupPatternUnit) {
      return value;
    }
    return SpawnGroupPatternUnit.fromJson(value);
  }
  static fromJson(json) {
    const result = new SpawnGroupPatternUnit();
    if (json.unit != null) {
      result._hasUnit = true;
      result._unit = JsonToString.fromJson(json.unit);
    }
    if (json.delay != null) {
      result._hasDelay = true;
      result._delay = JsonToDouble.fromJson(json.delay);
    }
    if (json.offset != null) {
      result._hasOffset = true;
      result._offset = JsonToDouble.fromJson(json.offset);
    }
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._hasUnit) {
      result.unit = JsonToString.toJson(this._unit);
    }
    if (this._hasDelay) {
      result.delay = JsonToDouble.toJson(this._delay);
    }
    if (this._hasOffset) {
      result.offset = JsonToDouble.toJson(this._offset);
    }
    return result;
  }
  /**
   * @returns {string}
   */
  get unit() {
    return this._hasUnit ? this._unit : JsonToString.default;
  }
  get hasUnit() {
    return this._hasUnit;
  }
  setUnit(path, value) {
    return {type: 'SET', path: path.concat(['unit']), value};
  }
  deleteUnit(path) {
    return {type: 'DELETE', path: path.concat(['unit'])};
  }
  /**
   * @returns {number}
   */
  get delay() {
    return this._hasDelay ? this._delay : JsonToDouble.default;
  }
  get hasDelay() {
    return this._hasDelay;
  }
  setDelay(path, value) {
    return {type: 'SET', path: path.concat(['delay']), value};
  }
  deleteDelay(path) {
    return {type: 'DELETE', path: path.concat(['delay'])};
  }
  /**
   * @returns {number}
   */
  get offset() {
    return this._hasOffset ? this._offset : JsonToDouble.default;
  }
  get hasOffset() {
    return this._hasOffset;
  }
  setOffset(path, value) {
    return {type: 'SET', path: path.concat(['offset']), value};
  }
  deleteOffset(path) {
    return {type: 'DELETE', path: path.concat(['offset'])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "unit": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new SpawnGroupPatternUnit(this);
              copy._hasUnit = true;
              copy._unit = JsonToString.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasUnit) {
                return this;
              }
              const copy = new SpawnGroupPatternUnit(this);
              copy._hasUnit = false;
              copy._unit = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "delay": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new SpawnGroupPatternUnit(this);
              copy._hasDelay = true;
              copy._delay = JsonToDouble.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasDelay) {
                return this;
              }
              const copy = new SpawnGroupPatternUnit(this);
              copy._hasDelay = false;
              copy._delay = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      case "offset": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new SpawnGroupPatternUnit(this);
              copy._hasOffset = true;
              copy._offset = JsonToDouble.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              if (!this._hasOffset) {
                return this;
              }
              const copy = new SpawnGroupPatternUnit(this);
              copy._hasOffset = false;
              copy._offset = null;
              return copy;
            }
            default:
              return this;
          }
        } else {
          return this;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {SpawnGroupPatternUnit} other
   * @returns {SpawnGroupPatternUnit}
   */
  merge(other) {
    const copy = new SpawnGroupPatternUnit(this);
    if (other._hasUnit) {
      copy._hasUnit = true;
      copy._unit = other._unit;
    }
    if (other._hasDelay) {
      copy._hasDelay = true;
      copy._delay = other._delay;
    }
    if (other._hasOffset) {
      copy._hasOffset = true;
      copy._offset = other._offset;
    }
    return copy;
  }
}
SpawnGroupPatternUnit.default = new SpawnGroupPatternUnit();
class SpawnGroupPatternUnit_Builder {
  constructor(value) {
    this._value = new SpawnGroupPatternUnit(value);
  }
  build() {
    return new SpawnGroupPatternUnit(this._value);
  }
  setUnit(value) {
    this._value._hasUnit = true;
    this._value._unit = value;
    return this;
  }
  deleteUnit() {
    this._value._hasUnit = false;
    this._value._unit = null;
    return this;
  }
  setDelay(value) {
    this._value._hasDelay = true;
    this._value._delay = value;
    return this;
  }
  deleteDelay() {
    this._value._hasDelay = false;
    this._value._delay = null;
    return this;
  }
  setOffset(value) {
    this._value._hasOffset = true;
    this._value._offset = value;
    return this;
  }
  deleteOffset() {
    this._value._hasOffset = false;
    this._value._offset = null;
    return this;
  }
}
SpawnGroupPatternUnit.Builder = SpawnGroupPatternUnit_Builder;
class SpawnGroupPatternUnit_WithParent {
  /**
   * @param {SpawnGroupPatternUnit} value
   * @param {SpawnGroupPatternUnit_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return SpawnGroupPatternUnit.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get hasUnit() {
    if (this._cachedHasUnit === undefined) {
      if (this._value.hasUnit) {
        this._cachedHasUnit = true;
      } else {
        this._cachedHasUnit = this._parent ? this._parent.hasUnit : false;
      }
    }
    return this._cachedHasUnit;
  }
  get unit() {
    if (this._cachedUnit === undefined) {
      if (this._value.hasUnit) {
        this._cachedUnit = this._value.unit;
      } else {
        this._cachedUnit = this._parent ? this._parent.unit : this._value.unit;
      }
    }
    return this._cachedUnit;
  }
  get hasDelay() {
    if (this._cachedHasDelay === undefined) {
      if (this._value.hasDelay) {
        this._cachedHasDelay = true;
      } else {
        this._cachedHasDelay = this._parent ? this._parent.hasDelay : false;
      }
    }
    return this._cachedHasDelay;
  }
  get delay() {
    if (this._cachedDelay === undefined) {
      if (this._value.hasDelay) {
        this._cachedDelay = this._value.delay;
      } else {
        this._cachedDelay = this._parent ? this._parent.delay : this._value.delay;
      }
    }
    return this._cachedDelay;
  }
  get hasOffset() {
    if (this._cachedHasOffset === undefined) {
      if (this._value.hasOffset) {
        this._cachedHasOffset = true;
      } else {
        this._cachedHasOffset = this._parent ? this._parent.hasOffset : false;
      }
    }
    return this._cachedHasOffset;
  }
  get offset() {
    if (this._cachedOffset === undefined) {
      if (this._value.hasOffset) {
        this._cachedOffset = this._value.offset;
      } else {
        this._cachedOffset = this._parent ? this._parent.offset : this._value.offset;
      }
    }
    return this._cachedOffset;
  }
  update(current, parent) {
    const result = new SpawnGroupPatternUnit_WithParent(current, parent);
    return result;
  }
}
SpawnGroupPatternUnit_WithParent.default = new SpawnGroupPatternUnit_WithParent(SpawnGroupPatternUnit.default, null);
SpawnGroupPatternUnit.WithParent = SpawnGroupPatternUnit_WithParent;
class SpawnGroupPatternUnit_Descriptor {
  get unit() {
    if (!this._unit) {
      this._unit = {
        name: "unit",
        Name: "Unit",
        number: 1,
        protoType: 9,
        protoName: "unit",
        type: JsonToString,
        options: {
          valueDropdown: "SupportedBaseUnitNames",
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._unit;
  }
  get delay() {
    if (!this._delay) {
      this._delay = {
        name: "delay",
        Name: "Delay",
        number: 2,
        protoType: 1,
        protoName: "delay",
        type: JsonToDouble,
        options: {
          min: 0,
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._delay;
  }
  get offset() {
    if (!this._offset) {
      this._offset = {
        name: "offset",
        Name: "Offset",
        number: 3,
        protoType: 1,
        protoName: "offset",
        type: JsonToDouble,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._offset;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.unit,
        this.delay,
        this.offset,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
      this._oneofs.push({
        name: "dummyOneofForUnit",
        Name: "DummyOneofForUnit",
        fields: [
          this.fields[0],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForDelay",
        Name: "DummyOneofForDelay",
        fields: [
          this.fields[1],
        ],
      });
      this._oneofs.push({
        name: "dummyOneofForOffset",
        Name: "DummyOneofForOffset",
        fields: [
          this.fields[2],
        ],
      });
    }
    return this._oneofs;
  }
}
SpawnGroupPatternUnit.descriptor = new SpawnGroupPatternUnit_Descriptor();
export class SpawnGroup {
  get descriptor() {
    return SpawnGroup.descriptor;
  }
  constructor(other) {
    if (other != null) {
      Object.assign(this, other);
      return;
    }
    this._spawnGroupPattern = [];
  }
  isEmpty() {
    if (this._spawnGroupPattern.length > 0) return false;
    return true;
  }
  static fromAny(value) {
    if (value instanceof SpawnGroup) {
      return value;
    }
    return SpawnGroup.fromJson(value);
  }
  static fromJson(json) {
    const result = new SpawnGroup();
    result._spawnGroupPattern = JsonToList(json.spawnGroupPattern, SpawnGroupPatternUnit);
    return result;
  }
  static toJson(value) {
    return value.toJson();
  }
  toJson() {
    const result = {};
    if (this._spawnGroupPattern.length > 0) {
      result.spawnGroupPattern = ArrayToJson(this._spawnGroupPattern, SpawnGroupPatternUnit);
    }
    return result;
  }
  /**
   * @returns {SpawnGroupPatternUnit[]}
   */
  get spawnGroupPattern() {
    return this._spawnGroupPattern;
  }
  addSpawnGroupPattern(path, value) {
    return {type: 'ADD', path: path.concat(['spawnGroupPattern']), value: value.toJson()};
  }
  setSpawnGroupPattern(path, value) {
    return {type: 'SET', path: path.concat(['spawnGroupPattern']), value: ArrayToJson(value, SpawnGroupPatternUnit)};
  }
  setSpawnGroupPatternByIndex(path, key, value) {
    return {type: 'SET', path: path.concat(['spawnGroupPattern', key]), value: value.toJson()};
  }
  deleteSpawnGroupPattern(path) {
    return {type: 'DELETE', path: path.concat(['spawnGroupPattern'])};
  }
  deleteSpawnGroupPatternByIndex(path, key) {
    return {type: 'DELETE', path: path.concat(['spawnGroupPattern', key])};
  }
  apply(type, path, value) {
    if (path.length === 0) {
      return this;
    }
    switch (path[0]) {
      case "spawnGroupPattern": {
        if (path.length === 1) {
          switch (type) {
            case 'SET': {
              const copy = new SpawnGroup(this);
              copy._spawnGroupPattern = [];        for (let i of value) {
                copy._spawnGroupPattern.push(SpawnGroupPatternUnit.fromAny(i));
              }
              return copy;
            }
            case 'ADD': {
              const copy = new SpawnGroup(this);
              copy._spawnGroupPattern = copy._spawnGroupPattern.concat([SpawnGroupPatternUnit.fromAny(value)]);
              return copy;
            }
            case 'DELETE': {
              if (this._spawnGroupPattern.length === 0) {
                return this;
              }
              const copy = new SpawnGroup(this);
              copy._spawnGroupPattern = [];
              return copy;
            }
            default:
              return this;
          }
        } else if (path.length === 2) {
          const key = path[1];
          if (key < 0 || key >= this._spawnGroupPattern.length) {
            return this;
          }
          switch (type) {
            case 'SET': {
              const copy = new SpawnGroup(this);
              copy._spawnGroupPattern = copy._spawnGroupPattern.slice();
              copy._spawnGroupPattern[path[1]] = SpawnGroupPatternUnit.fromAny(value);
              return copy;
            }
            case 'DELETE': {
              const copy = new SpawnGroup(this);
              copy._spawnGroupPattern = copy._spawnGroupPattern.slice(0, path[1]).concat(copy._spawnGroupPattern.slice(path[1] + 1));
              return copy;
            }
            default:
              return this;
          }
        } else {
          const key = path[1];
          if (key < 0 || key >= this._spawnGroupPattern.length) {
            return this;
          }
          const oldValue = this._spawnGroupPattern[key];
          const newValue = oldValue.apply(type, path.slice(2), value);
          if (oldValue === newValue) {
            return this;
          }
          const copy = new SpawnGroup(this);
          copy._spawnGroupPattern = copy._spawnGroupPattern.slice();
          copy._spawnGroupPattern[key] = newValue;
          return copy;
        }
      }
      default:
        return this;
    }
  }
  /**
   * @param {SpawnGroup} other
   * @returns {SpawnGroup}
   */
  merge(other) {
    const copy = new SpawnGroup(this);
    copy._spawnGroupPattern = this._spawnGroupPattern.concat(other._spawnGroupPattern);
    return copy;
  }
}
SpawnGroup.default = new SpawnGroup();
class SpawnGroup_Builder {
  constructor(value) {
    this._value = new SpawnGroup(value);
  }
  build() {
    return new SpawnGroup(this._value);
  }
  addAllSpawnGroupPattern(values) {
    this._value._spawnGroupPattern = this._value._spawnGroupPattern.concat(values);
    return this;
  }
  addSpawnGroupPattern(value) {
    this._value._spawnGroupPattern = this._value._spawnGroupPattern.concat([value]);
    return this;
  }
  setSpawnGroupPattern(index, value) {
    this._value._spawnGroupPattern = this._value._spawnGroupPattern.concat();
    this._value._spawnGroupPattern[index] = value;
    return this;
  }
  clearSpawnGroupPattern() {
    this._value._spawnGroupPattern = [];
    return this;
  }
}
SpawnGroup.Builder = SpawnGroup_Builder;
class SpawnGroup_WithParent {
  /**
   * @param {SpawnGroup} value
   * @param {SpawnGroup_WithParent} parent
   */
  constructor(value, parent) {
    this._value = value;
    this._parent = parent || null;
  }
  get descriptor() {
    return SpawnGroup.descriptor;
  }
  get selfValue() {
    return this._value;
  }
  get parentValue() {
    return this._parent;
  }
  get selfSpawnGroupPattern() {
    if (!this._selfSpawnGroupPattern) {
      this._selfSpawnGroupPattern = [];
      for (let i of this._value.spawnGroupPattern) {
        this._selfSpawnGroupPattern.push(new SpawnGroupPatternUnit.WithParent(i, null));
      }
    }
    return this._selfSpawnGroupPattern;
  }
  get inherentSpawnGroupPattern() {
    if (!this._inherentSpawnGroupPattern) {
      this._inherentSpawnGroupPattern = this._parent ? this._parent.spawnGroupPattern : [];
    }
    return this._inherentSpawnGroupPattern;
  }
  get spawnGroupPattern() {
    if (!this._allSpawnGroupPattern) {
      this._allSpawnGroupPattern = this._parent ? this._parent.spawnGroupPattern.concat(this.selfSpawnGroupPattern) : this.selfSpawnGroupPattern;
    }
    return this._allSpawnGroupPattern;
  }
  update(current, parent) {
    const result = new SpawnGroup_WithParent(current, parent);
    if (this._selfSpawnGroupPattern) {
      if (this._value.spawnGroupPattern === current.spawnGroupPattern) {
        result._selfSpawnGroupPattern = this._selfSpawnGroupPattern;
      } else {
        result._selfSpawnGroupPattern = [];
        for (let i = 0; i < current.spawnGroupPattern.length; i++) {
          const oldValue = this._value.spawnGroupPattern[i];
          if (oldValue) {
            const oldCache = this._selfSpawnGroupPattern[i];
            if (oldValue === current.spawnGroupPattern[i]) {
              result._selfSpawnGroupPattern[i] = oldCache;
            } else {
              result._selfSpawnGroupPattern[i] = oldCache.update(current.spawnGroupPattern[i], null);
            }
          } else {
            result._selfSpawnGroupPattern[i] = new SpawnGroupPatternUnit.WithParent(current.spawnGroupPattern[i], null);
          }
        }
      }
    }
    return result;
  }
}
SpawnGroup_WithParent.default = new SpawnGroup_WithParent(SpawnGroup.default, null);
SpawnGroup.WithParent = SpawnGroup_WithParent;
class SpawnGroup_Descriptor {
  get spawnGroupPattern() {
    if (!this._spawnGroupPattern) {
      this._spawnGroupPattern = {
        name: "spawnGroupPattern",
        Name: "SpawnGroupPattern",
        number: 1,
        protoType: 11,
        protoName: "spawn_group_pattern",
        type: SpawnGroupPatternUnit,
        isRepeated: true,
        options: {
        },
        /**
         * @type {import('react').PureComponent}
         */
        customEditor: null,
      };
    }
    return this._spawnGroupPattern;
  }
  get fields() {
    if (!this._fields) {
      this._fields = [
        this.spawnGroupPattern,
      ];
    }
    return this._fields;
  }
  get oneofs() {
    if (!this._oneofs) {
      this._oneofs = [];
    }
    return this._oneofs;
  }
}
SpawnGroup.descriptor = new SpawnGroup_Descriptor();
